import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getUserOrders, getProducts } from '../services/api';
import OrderForm from './OrderForm';
import Deals from './Deals';
import UpdateOrderModal from './UpdateOrderModal';
import CancelOrderModal from './CancelOrderModal';
import TermsAndConditions from './TermsAndConditions';
import SampleScreenshots from './SampleScreenshots';
import Footer from './Footer';
import AdGate from './AdGate';
import './Dashboard.css';

const Dashboard = ({ user, setUser }) => {
    const [orders, setOrders] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [productsLoading, setProductsLoading] = useState(true);
    const [error, setError] = useState('');
    const location = useLocation();
    const [activeTab, setActiveTab] = useState(location.state?.tab || 'deals'); // 'orders', 'submit', 'deals', 'tnc', 'sample-ss'
    const [showAdGate, setShowAdGate] = useState(false);

    // Update active tab if location state changes
    useEffect(() => {
        if (location.state?.tab) {
            setActiveTab(location.state.tab);
        }
    }, [location.state]);

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [selectedOrderForCancel, setSelectedOrderForCancel] = useState(null);
    const [commentModal, setCommentModal] = useState({ isOpen: false, text: '' });
    const [isDrawerOpen, setIsDrawerOpen] = useState(false); // State for side drawer
    const navigate = useNavigate();
    const ordersSectionRef = useRef(null); // Keep this ref for scrolling
    const previousPage = useRef(currentPage); // Add the new ref for page tracking

    // Scroll to top of orders when page changes
    useEffect(() => {
        if (previousPage.current !== currentPage) {
            if (ordersSectionRef.current) {
                ordersSectionRef.current.scrollIntoView({ behavior: 'smooth' });
            }
            previousPage.current = currentPage;
        }
    }, [currentPage]);

    useEffect(() => {
        // Show Ad Gate every time user enters dashboard
        // setShowAdGate(true); // Commented out for now as requested

        if (user) {
            fetchAllData();
        } else {
            setLoading(false);
            setProductsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const handleAdUnlock = () => {
        setShowAdGate(false);
    };

    const fetchAllData = async () => {
        setLoading(true);
        setProductsLoading(true);
        setError('');

        try {
            await Promise.all([fetchOrders(false), fetchProducts(false)]);
        } catch (err) {
            setError('An error occurred while fetching data');
        } finally {
            setLoading(false);
            setProductsLoading(false);
        }
    };

    const fetchOrders = async (setLoadingState = true) => {
        if (setLoadingState) setLoading(true);
        setError('');

        try {
            const response = await getUserOrders(user.buyerId);

            if (response.success) {
                const fetchedOrders = response.data.orders || [];
                // Sort by timestamp descending (newest first)
                fetchedOrders.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
                setOrders(fetchedOrders);
            } else {
                setError(response.message || 'Failed to fetch orders');
            }
        } catch (err) {
            setError('An unexpected error occurred while fetching orders');
        } finally {
            if (setLoadingState) setLoading(false);
        }
    };

    const fetchProducts = async (setLoadingState = true) => {
        if (setLoadingState) setProductsLoading(true);
        try {
            const response = await getProducts();
            if (response.success) {
                setProducts(response.data.products || []);
            } else {
                console.error('Failed to fetch products:', response.message);
            }
        } catch (err) {
            console.error('Error fetching products:', err);
        } finally {
            if (setLoadingState) setProductsLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('cwUser');
        setUser(null);
        navigate('/');
    };

    const handleRefresh = () => {
        fetchAllData();
    };

    const handleOpenUpdateModal = (order) => {
        setSelectedOrder(order);
        setIsUpdateModalOpen(true);
    };

    const handleCloseUpdateModal = () => {
        setIsUpdateModalOpen(false);
        setSelectedOrder(null);
    };

    const handleUpdateSuccess = () => {
        fetchOrders(); // Refresh orders after update
        handleCloseUpdateModal();
    };

    const handleOpenCancelModal = (order) => {
        setSelectedOrderForCancel(order);
        setIsCancelModalOpen(true);
    };

    const handleCloseCancelModal = () => {
        setIsCancelModalOpen(false);
        setSelectedOrderForCancel(null);
    };

    const handleCancelSuccess = () => {
        fetchOrders();
        handleCloseCancelModal();
    };

    const handleOpenCommentModal = (text) => {
        setCommentModal({
            isOpen: true,
            text: text || 'No comment provided.'
        });
    };

    const handleCloseCommentModal = () => {
        setCommentModal({ isOpen: false, text: '' });
    };

    const toggleDrawer = () => {
        setIsDrawerOpen(!isDrawerOpen);
    };

    const handleDrawerNavigation = (tab) => {
        setActiveTab(tab);
        setIsDrawerOpen(false);
    };

    // Calculate pagination values
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentOrders = orders.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(orders.length / itemsPerPage);

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    // Calculate summary statistics
    const activeOrders = orders.filter(order =>
        order.orderStatus !== 'Cancelled' &&
        order.affiliateStatus !== 'Cancelled'
    );

    const totalOrders = activeOrders.length;

    // Paid orders: affiliate status is "Paid"
    const paidOrders = activeOrders.filter(order => order.affiliateStatus === 'Paid').length;

    // Total Paid Amount: Sum of refundAmount for orders with affiliateStatus 'Paid'
    const totalPaidAmount = activeOrders.reduce((sum, order) => {
        if (order.affiliateStatus === 'Paid') {
            const amount = parseFloat(order.refundAmount);
            return sum + (isNaN(amount) ? 0 : amount);
        }
        return sum;
    }, 0);

    // Expired orders: affiliate status is "Pending" AND more than 20 days old
    const expiredOrders = activeOrders.filter(order => {
        if (!order.timestamp) return false;

        // Only count as expired if affiliate status is "Pending"
        if (order.affiliateStatus !== 'Pending') return false;

        const orderDate = new Date(order.timestamp);
        const daysDiff = Math.floor((new Date() - orderDate) / (1000 * 60 * 60 * 24));
        return daysDiff > 20;
    }).length;

    return (
        <div className="dashboard-container">
            {showAdGate && <AdGate onUnlock={handleAdUnlock} />}
            {/* Navigation */}
            <nav className="home-nav">
                <div className="nav-content">
                    <div className="logo" onClick={() => navigate('/')}>
                        <img src="/cwLogo.png" alt="Collaboration World" className="navbar-logo-image" />
                    </div>
                    <div className="nav-links">
                        {/* <button onClick={() => navigate('/')} className="nav-link">Home</button>
                        <button onClick={() => navigate('/about')} className="nav-link">About Us</button> */}
                        <div
                            className="profile-section"
                            onClick={() => navigate('/profile')}
                            title="View Profile"
                        >
                            <div className="profile-icon-wrapper">
                                <i className="ri-user-line"></i>
                            </div>
                            <div className="profile-info">
                                <span className="profile-name-text">{user.fullName}</span>
                                <span className="profile-id">ID: {user.buyerId}</span>
                            </div>
                        </div>
                        <button onClick={handleLogout} className="logout-icon" title="Sign Out">
                            <i className="ri-logout-box-r-line"></i>
                        </button>

                        {/* Hamburger Button for Dashboard Drawer */}
                        <button className="dashboard-hamburger" onClick={toggleDrawer}>
                            <i className="ri-menu-line"></i>
                        </button>
                    </div>
                </div>
            </nav>

            <div className="dashboard-tabs">
                <button
                    className={`tab ${activeTab === 'deals' ? 'active' : ''}`}
                    onClick={() => setActiveTab('deals')}
                >
                    <i className="ri-gift-line"></i> Deals
                </button>
                <button
                    className={`tab ${activeTab === 'orders' ? 'active' : ''}`}
                    onClick={() => setActiveTab('orders')}
                >
                    <i className="ri-file-list-3-line"></i> My Orders
                </button>
                <button
                    className={`tab ${activeTab === 'submit' ? 'active' : ''}`}
                    onClick={() => setActiveTab('submit')}
                >
                    <i className="ri-add-circle-line"></i> Add Order
                </button>
                <button
                    className={`tab ${activeTab === 'sample-ss' ? 'active' : ''}`}
                    onClick={() => setActiveTab('sample-ss')}
                >
                    <i className="ri-image-line"></i> Samples
                </button>
                <button
                    className={`tab ${activeTab === 'tnc' ? 'active' : ''}`}
                    onClick={() => setActiveTab('tnc')}
                >
                    <i className="ri-file-shield-2-line"></i> T&C
                </button>

            </div>

            <div className="dashboard-content">
                {activeTab === 'deals' ? (
                    <Deals products={products} loading={productsLoading} onRefresh={handleRefresh} onAddOrder={() => setActiveTab('submit')} />
                ) : activeTab === 'submit' ? (
                    <OrderForm user={user} setActiveTab={setActiveTab} fetchOrders={handleRefresh} products={products} />
                ) : activeTab === 'tnc' ? (
                    <TermsAndConditions />
                ) : activeTab === 'sample-ss' ? (
                    <SampleScreenshots />
                ) : (
                    <>
                        <div className="summary-cards">
                            <div className="summary-card">
                                <div className="card-icon"><i className="ri-box-3-line"></i></div>
                                <div className="card-content">
                                    <p className="card-label">Total Orders</p>
                                    <p className="card-value">
                                        {loading ? <span className="skeleton-text skeleton-pulse"></span> : totalOrders}
                                    </p>
                                </div>
                            </div>

                            <div className="summary-card success">
                                <div className="card-icon"><i className="ri-checkbox-circle-line"></i></div>
                                <div className="card-content">
                                    <p className="card-label">Paid Orders</p>
                                    <p className="card-value">
                                        {loading ? <span className="skeleton-text skeleton-pulse"></span> : paidOrders}
                                    </p>
                                </div>
                            </div>

                            <div className="summary-card warning">
                                <div className="card-icon"><i className="ri-money-dollar-circle-line"></i></div>
                                <div className="card-content">
                                    <p className="card-label">Total Paid Amt</p>
                                    <p className="card-value">
                                        {loading ? <span className="skeleton-text skeleton-pulse"></span> : `₹${totalPaidAmount.toLocaleString()}`}
                                    </p>
                                </div>
                            </div>

                            <div className="summary-card error">
                                <div className="card-icon"><i className="ri-error-warning-line"></i></div>
                                <div className="card-content">
                                    <p className="card-label">Expired Orders</p>
                                    <p className="card-value">
                                        {loading ? <span className="skeleton-text skeleton-pulse"></span> : expiredOrders}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="orders-section" ref={ordersSectionRef}>
                            <div className="section-header">
                                <h2>Order History</h2>
                                <button onClick={handleRefresh} className="btn-refresh" disabled={loading}>
                                    {loading ?
                                        <><i className="ri-loader-4-line ri-spin"></i> <span className="btn-text">Refreshing...</span></>
                                        :
                                        <><i className="ri-refresh-line"></i> <span className="btn-text">Refresh</span></>
                                    }
                                </button>
                            </div>

                            {error && <div className="error-message">{error}</div>}

                            {loading ? (
                                <div className="loading-state">Loading orders...</div>
                            ) : orders.length === 0 ? (
                                <div className="empty-state">
                                    <div className="empty-icon"><i className="ri-inbox-line"></i></div>
                                    <p>No orders yet</p>
                                    <p className="empty-hint">
                                        Click "Add Order" to add your first order
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <div className="orders-table-container">
                                        <table className="orders-table">
                                            <thead>
                                                <tr>
                                                    <th>Actions</th>
                                                    <th>Order Status</th>
                                                    <th>Affiliate Status</th>
                                                    <th>Date</th>
                                                    <th>Brand</th>
                                                    <th>Product</th>
                                                    <th>Profile Name</th>
                                                    <th>Order ID</th>
                                                    <th>Total</th>
                                                    <th>Refund Amount</th>
                                                    <th>Order Screenshot</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentOrders.map((order, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <div className="action-buttons-container" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                                                {order.affiliateStatus !== 'Paid' && order.affiliateStatus !== 'Accepted' && order.affiliateStatus !== 'Cancelled' && (
                                                                    <button
                                                                        onClick={() => handleOpenUpdateModal(order)}
                                                                        className="btn-update"
                                                                        title={order.orderStatus === 'Accepted' ? 'Update affiliate details' : 'Update order details'}
                                                                    >
                                                                        Update
                                                                    </button>
                                                                )}
                                                                {order.orderStatus !== 'Cancelled' && order.affiliateStatus === 'Pending' && (
                                                                    <button
                                                                        onClick={() => handleOpenCancelModal(order)}
                                                                        className="btn-cancel-order"
                                                                        title="Cancel this order"
                                                                    >
                                                                        Cancel
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span
                                                                className={`status-badge status-${order.orderStatus?.toLowerCase()} ${order.comment ? 'clickable' : ''}`}
                                                                onClick={() => order.comment && handleOpenCommentModal(order.comment)}
                                                                title={order.comment ? 'Click to view comment' : ''}
                                                            >
                                                                {order.orderStatus}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span
                                                                className={`status-badge affiliate-${order.affiliateStatus?.toLowerCase()} ${order.comment ? 'clickable' : ''}`}
                                                                onClick={() => order.comment && handleOpenCommentModal(order.comment)}
                                                                title={order.comment ? 'Click to view comment' : ''}
                                                            >
                                                                {order.affiliateStatus}
                                                            </span>
                                                        </td>
                                                        <td>{new Date(order.timestamp).toLocaleDateString('en-IN')}</td>
                                                        <td className="brand-name">{order.brandName}</td>
                                                        <td className="product-name">
                                                            <div className="product-name-content" title={order.productName}>
                                                                {order.productName}
                                                            </div>
                                                        </td>
                                                        <td className="profile-name-cell">{order.fullName}</td>
                                                        <td className="order-id">{order.orderId}</td>
                                                        <td className="amount">₹{parseFloat(order.orderTotal).toFixed(2)}</td>
                                                        <td className="amount refunded">
                                                            <div className="refund-main">₹{parseFloat(order.refundAmount || 0).toFixed(2)}</div>
                                                            {(parseFloat(order.lessAmount || order.balance) > 0) && (
                                                                <div className="balance-info">
                                                                    (Less: ₹{parseFloat(order.lessAmount || order.balance).toFixed(2)})
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <a
                                                                href={order.screenshotLink}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="screenshot-link"
                                                            >
                                                                View
                                                            </a>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination Controls */}
                                    {orders.length > itemsPerPage && (
                                        <div className="pagination-controls">
                                            <button
                                                className="pagination-btn"
                                                onClick={handlePrevPage}
                                                disabled={currentPage === 1}
                                            >
                                                <i className="ri-arrow-left-s-line"></i> Previous
                                            </button>
                                            <span className="pagination-info">
                                                Page {currentPage} of {totalPages}
                                            </span>
                                            <button
                                                className="pagination-btn"
                                                onClick={handleNextPage}
                                                disabled={currentPage === totalPages}
                                            >
                                                Next <i className="ri-arrow-right-s-line"></i>
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Footer */}
            <Footer />

            {/* Update Order Modal */}
            <UpdateOrderModal
                isOpen={isUpdateModalOpen}
                onClose={handleCloseUpdateModal}
                order={selectedOrder}
                user={user}
                onSuccess={handleUpdateSuccess}
                products={products}
            />

            {/* Cancel Order Modal */}
            <CancelOrderModal
                isOpen={isCancelModalOpen}
                onClose={handleCloseCancelModal}
                order={selectedOrderForCancel}
                user={user}
                onSuccess={handleCancelSuccess}
            />

            {/* Comment Details Modal */}
            {
                commentModal.isOpen && (
                    <div className="rejection-modal-overlay" onClick={handleCloseCommentModal}>
                        <div className="rejection-modal" onClick={e => e.stopPropagation()}>
                            <h3>
                                <i className="ri-chat-1-line"></i> Comment
                            </h3>
                            <p>{commentModal.text}</p>
                            <button className="btn-close-modal" onClick={handleCloseCommentModal}>
                                Close
                            </button>
                        </div>
                    </div>
                )
            }

            {/* Dashboard Side Drawer */}
            <div className={`dashboard-drawer-overlay ${isDrawerOpen ? 'active' : ''}`} onClick={() => setIsDrawerOpen(false)}></div>
            <div className={`dashboard-drawer ${isDrawerOpen ? 'active' : ''}`}>
                <div className="drawer-header">
                    <h3>Menu</h3>
                    <button className="close-drawer-btn" onClick={() => setIsDrawerOpen(false)}>
                        <i className="ri-close-line"></i>
                    </button>
                </div>
                <div className="drawer-content">
                    <button
                        className={`drawer-link ${activeTab === 'deals' ? 'active' : ''}`}
                        onClick={() => handleDrawerNavigation('deals')}
                    >
                        <i className="ri-gift-line"></i> Deals
                    </button>
                    <button
                        className={`drawer-link ${activeTab === 'orders' ? 'active' : ''}`}
                        onClick={() => handleDrawerNavigation('orders')}
                    >
                        <i className="ri-file-list-3-line"></i> My Orders
                    </button>
                    <button
                        className={`drawer-link ${activeTab === 'submit' ? 'active' : ''}`}
                        onClick={() => handleDrawerNavigation('submit')}
                    >
                        <i className="ri-add-circle-line"></i> Add Order
                    </button>
                    <button
                        className="drawer-link"
                        onClick={() => {
                            navigate('/profile');
                            setIsDrawerOpen(false);
                        }}
                    >
                        <i className="ri-user-line"></i> My Profile
                    </button>

                    <button
                        className={`drawer-link ${activeTab === 'sample-ss' ? 'active' : ''}`}
                        onClick={() => handleDrawerNavigation('sample-ss')}
                    >
                        <i className="ri-image-line"></i> Samples
                    </button>
                    <button
                        className={`drawer-link ${activeTab === 'tnc' ? 'active' : ''}`}
                        onClick={() => handleDrawerNavigation('tnc')}
                    >
                        <i className="ri-file-shield-2-line"></i> T&C
                    </button>
                    <button
                        className="drawer-link"
                        onClick={handleLogout}
                        style={{ marginTop: 'auto', color: 'var(--cartier-burgundy)' }}
                    >
                        <i className="ri-logout-box-r-line"></i> Logout
                    </button>
                </div>
            </div>
        </div >
    );
};

export default Dashboard;
